import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Modal from "../../uiComponents/Modal";
import Shimmer from "../../shimmer/Shimmer";
import { ContentLog } from "../../../types/dashboard";
import useContentStatsApi from "../../../api/useContentStatsApi";
import { useTimeUtil } from "../../../hooks/useTimeUtil";
import { SOCIAL_PLATFORMS } from "../../../utils/platform-post-util";
import "./dashboard.css";

const PAGE_SIZE = 10;

interface ContentStatusListModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: "SCHEDULED" | "DRAFT" | "PROCESSING" | "FAILED" | "PARTIAL_FAILURE";
  title: string;
  onViewItem: (log: ContentLog) => void;
}

/** Normalize platform to string (API may return string or { platform, accountId }) */
function getPlatformId(platform: string | { platform?: string }): string {
  if (typeof platform === "string") return platform;
  return platform?.platform ?? "";
}

function getIconForPlatform(
  platform: string | { platform?: string },
): string | null {
  const p = getPlatformId(platform).toLowerCase();
  return SOCIAL_PLATFORMS.find((x) => x.id.toLowerCase() === p)?.icon ?? null;
}

const ContentStatusListModal: React.FC<ContentStatusListModalProps> = ({
  isOpen,
  onClose,
  status,
  title,
  onViewItem,
}) => {
  const { getHistoryByStatus } = useContentStatsApi();
  const { formatDate } = useTimeUtil();
  const [list, setList] = useState<ContentLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalLogs / PAGE_SIZE));

  const fetchList = useCallback(
    (pageNum: number) => {
      if (!isOpen || !status) return;
      setLoading(true);
      setError(null);
      const offset = (pageNum - 1) * PAGE_SIZE;
      getHistoryByStatus(status, offset, PAGE_SIZE)
        .then(({ list: data, total_logs }) => {
          setList(data);
          setTotalLogs(total_logs);
        })
        .catch(() => setError("Failed to load items"))
        .finally(() => setLoading(false));
    },
    [isOpen, status, getHistoryByStatus],
  );

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchList(1);
    } else {
      setList([]);
      setTotalLogs(0);
      setPage(1);
      setError(null);
    }
  }, [isOpen, status, fetchList]);

  const goToPrevPage = useCallback(() => {
    if (page <= 1 || loading) return;
    const nextPage = page - 1;
    setPage(nextPage);
    fetchList(nextPage);
  }, [page, loading, fetchList]);

  const goToNextPage = useCallback(() => {
    if (page >= totalPages || loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchList(nextPage);
  }, [page, totalPages, loading, fetchList]);

  const handleView = useCallback(
    (log: ContentLog) => {
      onViewItem(log);
      onClose();
    },
    [onViewItem, onClose],
  );

  const contentLogsShimmer = () => (
    <div className="ds-status-modal-scroll">
      <table className="ds-status-table">
        <thead>
          <tr>
            <th>
              <Shimmer width={60} />
            </th>
            <th>
              <Shimmer width={80} />
            </th>
            <th>
              <Shimmer width={60} />
            </th>
            <th>
              <Shimmer width={60} />
            </th>
            <th>
              <Shimmer width={40} />
            </th>
            <th>
              <Shimmer width={50} />
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              <td>
                <Shimmer width={120} />
              </td>
              <td>
                <Shimmer width={180} />
              </td>
              <td>
                <Shimmer width={80} />
              </td>
              <td>
                <Shimmer width={100} />
              </td>
              <td>
                <Shimmer width={120} />
              </td>
              <td>
                <Shimmer width={40} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const headerTitle = (
    <div className="ds-status-modal-header-inner">
      <span className="ds-status-modal-title-text">{title}</span>
      {totalLogs > 0 && (
        <div className="ds-status-modal-pagination ds-status-modal-pagination-in-header">
          <button
            type="button"
            onClick={goToPrevPage}
            disabled={page <= 1 || loading}
            aria-label="Previous page"
            className="ds-status-modal-pagination-btn"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="ds-status-modal-pagination-label">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={goToNextPage}
            disabled={page >= totalPages || loading}
            aria-label="Next page"
            className="ds-status-modal-pagination-btn"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      size="large"
      className="ds-status-modal-wrap"
    >
      <div className="ds-status-modal">
        {loading ? (
          contentLogsShimmer()
        ) : error ? (
          <div className="ds-status-modal-error">{error}</div>
        ) : list.length === 0 && page === 1 ? (
          <div className="ds-status-modal-empty">No items found.</div>
        ) : (
          <>
            <div className="ds-status-modal-scroll">
              <table className="ds-status-table">
                <thead>
                  <tr>
                    <th>BRAND</th>
                    <th>TOPIC</th>
                    <th>PLATFORMS</th>
                    <th>STATUS</th>
                    <th>DATE</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((gen) => (
                    <tr key={gen.id}>
                      <td className="ds-status-cell-brand">{gen.brand_name}</td>
                      <td className="ds-status-cell-topic">{gen.topic}</td>
                      <td>
                        <div className="ds-status-platforms">
                          {(gen.content?.platforms ?? []).map((p, idx) => {
                            const platformId = getPlatformId(p);
                            const icon = getIconForPlatform(p);
                            return icon ? (
                              <img
                                key={platformId || idx}
                                src={icon}
                                alt={platformId}
                                className="ds-status-platform-icon"
                              />
                            ) : null;
                          })}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`ds-status-badge ds-status-badge-${(
                            gen.status || ""
                          ).toLowerCase()}`}
                        >
                          {gen.status
                            ? gen.status.charAt(0).toUpperCase() +
                              gen.status.slice(1).toLowerCase()
                            : "-"}
                        </span>
                        {gen.content?.pfm_result?.failure_reason && (
                          <div
                            style={{
                              marginTop: "4px",
                              color: "#b91c1c",
                              fontSize: "12px",
                              lineHeight: 1.4,
                              maxWidth: "220px",
                            }}
                          >
                            {gen.content.pfm_result.failure_reason}
                          </div>
                        )}
                      </td>
                      <td className="ds-status-cell-date">
                        {formatDate(gen.created_at, "YYYY-MM-DD HH:mm:ss")}
                      </td>
                      <td>
                        <span
                          className="ds-status-view-action"
                          onClick={() => handleView(gen)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleView(gen)
                          }
                        >
                          View
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ContentStatusListModal;
